/**
 * @fileoverview Certificate issuance form component
 */
import { useState, useEffect } from 'react';
import { CERTIFICATE_TYPES, CERTIFICATE_MODES, MAINTENANCE_TYPES } from '@/utils/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import clsx from 'clsx';
export const CertificateForm = ({ formData, mode, setMode, updateField, errors, onSubmit, isLoading = false }) => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [issuanceMethod, setIssuanceMethod] = useState('form');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBasicData, setPdfBasicData] = useState({
    nameOrCompany: '',
    email: '',
  });

  // Beneficiary (Student) Selection
  const [students, setStudents] = useState([]);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Fetch students on mount
  useEffect(() => {
    if (issuanceMethod === 'form') {
      fetchStudents();
    }
  }, [issuanceMethod]);

  const fetchStudents = async () => {
    console.log('🔍 [CertificateForm] Fetching students...');
    setLoadingStudents(true);
    try {
      const response = await fetch('/api/users/students', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [CertificateForm] Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 [CertificateForm] Data:', data);
        console.log('🔍 [CertificateForm] Total students:', data.students?.length);
        // Only show students with wallet
        const eligible = data.students.filter(s => s.hasWallet);
        console.log('🔍 [CertificateForm] Eligible (with wallet):', eligible.length);
        console.log('🔍 [CertificateForm] Students:', eligible);
        setStudents(eligible);
      }
    } catch (error) {
      console.error('❌ [CertificateForm] Error:', error);
    } finally {
      setLoadingStudents(false);
      console.log('🔍 [CertificateForm] Fetch completed');
    }
  };
  const handleMethodChange = (method) => {
    setIssuanceMethod(method);
    if (method === 'pdf') {
      setPdfFile(null);
      setPdfBasicData({ nameOrCompany: '', email: '' });
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (issuanceMethod === 'pdf') {
      if (pdfFile && pdfBasicData.nameOrCompany && pdfBasicData.email) {
        onSubmit({ pdfFile, mode, nameOrCompany: pdfBasicData.nameOrCompany, email: pdfBasicData.email });
      }
    }
    else {
      // Beneficiary is mandatory for blockchain issuance
      if (!selectedBeneficiary) {
        alert(t('cert_form.beneficiary_required', 'Debe seleccionar un beneficiario para emitir el certificado.'));
        return;
      }
      const submitData = {
        ...formData,
        beneficiaryUserId: selectedBeneficiary
      };
      onSubmit(submitData);
    }
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
    }
    else {
      alert(t('cert_form.invalid_pdf', 'Por favor, seleccione un archivo PDF válido.'));
    }
  };
  const currentModeConfig = CERTIFICATE_MODES[mode];
  return (<div className="form-panel">
    <div className="panel-header">
      <h2>{t('cert_form.title', 'Emitir Nuevo Certificado')} - {currentModeConfig.label}</h2>
      <p>{t('cert_form.subtitle', 'Complete los datos para generar un registro oficial e inalterable.')}</p>
    </div>

    <div className="tabs" style={{ marginBottom: '25px' }}>
      {['pdf', 'form'].map((method) => (<button key={method} type="button" onClick={() => handleMethodChange(method)} className={clsx('tab', issuanceMethod === method && 'active')}>
        {method === 'pdf' ? t('cert_form.basic', 'Básico') : t('cert_form.complete', 'Completo')}
      </button>))}
    </div>

    <form onSubmit={handleSubmit}>
      {issuanceMethod === 'pdf' ? (<>
        <div className="form-group">
          <label className="label">
            {mode === 'pilot' ? t('cert_form.full_name', 'Nombre y Apellido') : t('cert_form.company_name', 'Razón Social')} <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input type="text" className={clsx('input-field', errors.nameOrCompany && 'error')} placeholder={mode === 'pilot' ? 'Ej. Juan Pérez' : 'Ej. Empresa S.A.'} value={pdfBasicData.nameOrCompany} onChange={(e) => setPdfBasicData({ ...pdfBasicData, nameOrCompany: e.target.value })} />
          {errors.nameOrCompany && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.nameOrCompany}</p>)}
        </div>

        <div className="form-group">
          <label className="label">
            Email <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input type="email" className={clsx('input-field', errors.email && 'error')} placeholder="Ej. juan.perez@email.com" value={pdfBasicData.email} onChange={(e) => setPdfBasicData({ ...pdfBasicData, email: e.target.value })} />
          {errors.email && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.email}</p>)}
        </div>

        <div className="form-group">
          <label className="label">
            {t('cert_form.pdf_certificate', 'Certificado PDF')} <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div className="file-upload-area" style={{
            border: '1px dashed #cbd5e1',
            padding: '20px',
            textAlign: 'left',
            borderRadius: '12px',
            cursor: 'pointer',
            background: pdfFile ? '#f0f9ff' : '#f8fafc',
            borderColor: pdfFile ? '#3b82f6' : '#cbd5e1',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
            overflow: 'hidden',
          }} onClick={() => document.getElementById('pdf-upload')?.click()} onMouseEnter={(e) => {
            if (!pdfFile) {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.background = '#f1f5f9';
            }
          }} onMouseLeave={(e) => {
            if (!pdfFile) {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.background = '#f8fafc';
            }
          }}>
            <input id="pdf-upload" type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} />

            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: pdfFile ? 'rgba(59, 130, 246, 0.1)' : '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className={`fas ${pdfFile ? 'fa-file-pdf' : 'fa-cloud-upload-alt'}`} style={{
                fontSize: '18px',
                color: pdfFile ? '#3b82f6' : '#64748b',
              }}></i>
            </div>

            <div style={{ flex: 1 }}>
              {pdfFile ? (<div>
                <p style={{ fontSize: '14px', color: '#1e293b', margin: '0 0 2px 0', fontWeight: '600' }}>
                  {pdfFile.name}
                </p>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  {(pdfFile.size / 1024 / 1024).toFixed(2)} MB — {t('cert_form.ready', 'Listo')}
                </p>
              </div>) : (<div>
                <p style={{ fontSize: '14px', color: '#1e293b', margin: '0 0 2px 0', fontWeight: '500' }}>
                  {t('cert_form.select_pdf', 'Seleccionar archivo PDF')}
                </p>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  {t('cert_form.click_browse', 'Haga clic para buscar (Máx. 10MB)')}
                </p>
              </div>)}
            </div>
          </div>
          {errors.pdfFile && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.pdfFile}</p>)}
        </div>

        <div style={{
          position: 'sticky',
          bottom: '-32px',
          margin: '24px -32px -32px -32px',
          padding: '16px 32px',
          background: 'white',
          borderTop: '1px solid #f1f5f9',
          borderRadius: '0 0 16px 16px',
          zIndex: 10
        }}>
          <button type="submit" disabled={isLoading || !pdfFile || !pdfBasicData.nameOrCompany || !pdfBasicData.email} className="btn-submit" style={{ width: '100%', marginTop: 0 }}>
            <i className="fas fa-paper-plane"></i>
            {isLoading ? t('cert_form.issuing', 'Emitiendo...') : t('cert_form.issue_cert', 'Emitir Certificado')}
          </button>
        </div>
      </>) : mode === 'pilot' ? (<>
        <div className="grid-2">
          <div className="form-group">
            <label className="label">
              {t('cert_form.beneficiary_name', 'Nombre del Beneficiario')}
            </label>
            <input type="text" className={clsx('input-field', errors.beneficiaryName && 'error')} placeholder={currentModeConfig.namePlaceholder} value={formData.beneficiaryName} onChange={(e) => updateField('beneficiaryName', e.target.value)} />
            {errors.beneficiaryName && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.beneficiaryName}</p>)}
          </div>

          <div className="form-group">
            <label className="label">
              {t('cert_form.id_license', 'ID / Licencia / Matrícula')}
            </label>
            <input type="text" className={clsx('input-field', errors.idOrLicense && 'error')} placeholder={currentModeConfig.idPlaceholder} value={formData.idOrLicense} onChange={(e) => updateField('idOrLicense', e.target.value)} />
            {errors.idOrLicense && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.idOrLicense}</p>)}
          </div>
        </div>

        {/* BENEFICIARY SELECTOR - NUEVO */}
        <div className="form-group" style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)',
          border: '2px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <i className="fas fa-user-graduate" style={{ color: '#3b82f6', fontSize: '18px' }}></i>
            <label className="label" style={{ margin: 0, fontWeight: 600, color: '#1e293b' }}>
              {t('cert_form.beneficiary_label', 'Beneficiario (Estudiante)')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
          </div>

          {loadingStudents ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '14px'
            }}>
              <i className="fas fa-spinner fa-spin"></i> {t('common.loading', 'Cargando...')}
            </div>
          ) : students.length === 0 ? (
            <div style={{
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#dc2626'
            }}>
              <i className="fas fa-exclamation-triangle"></i> {t('cert_form.no_students_wallet', 'No hay estudiantes con wallet disponibles. Los estudiantes deben crear su wallet primero.')}
            </div>
          ) : (
            <>
              <select
                className={clsx('select-field', !selectedBeneficiary && 'error')}
                value={selectedBeneficiary}
                onChange={(e) => setSelectedBeneficiary(e.target.value)}
                style={{
                  background: 'white',
                  borderColor: selectedBeneficiary ? '#3b82f6' : '#cbd5e1'
                }}
              >
                <option value="">{t('cert_form.select_student', '-- Seleccionar estudiante --')}</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.fullName} - {student.email} ({student.walletAddress?.substring(0, 10)}...)
                  </option>
                ))}
              </select>

              {selectedBeneficiary && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  {(() => {
                    const selected = students.find(s => s.id === selectedBeneficiary);
                    return selected ? (
                      <>
                        <div style={{ marginBottom: '6px' }}>
                          <strong style={{ color: '#1e293b' }}>{t('cert_form.selected', 'Seleccionado')}:</strong> {selected.fullName}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                          <i className="fas fa-wallet"></i> {selected.walletAddress}
                        </div>
                      </>
                    ) : null;
                  })()}
                </div>
              )}

              {!selectedBeneficiary && (
                <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '8px' }}>
                  {t('cert_form.beneficiary_required_hint', 'Debe seleccionar un beneficiario para poder emitir el certificado')}
                </p>
              )}
            </>
          )}

          <p style={{
            fontSize: '11px',
            color: '#64748b',
            marginTop: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <i className="fas fa-info-circle"></i>
            {t('cert_form.wallet_encrypt_note', 'El certificado será encriptado con la clave del estudiante, permitiéndole acceder desde su wallet.')}
          </p>
        </div>

        <div className="form-group">
          <label className="label">
            {t('cert_form.cert_type', 'Tipo de Certificación')}
          </label>
          <select className={clsx('select-field', errors.certificateType && 'error')} value={formData.certificateType} onChange={(e) => updateField('certificateType', e.target.value)}>
            {CERTIFICATE_TYPES.map((type) => (<option key={type.value} value={type.value}>
              {type.label}
            </option>))}
          </select>
          {errors.certificateType && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.certificateType}</p>)}
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="label">{t('cert_form.issue_date', 'Fecha de Emisión')}</label>
            <input type="date" className={clsx('input-field', errors.issueDate && 'error')} value={formData.issueDate} onChange={(e) => updateField('issueDate', e.target.value)} />
            {errors.issueDate && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.issueDate}</p>)}
          </div>

          <div className="form-group">
            <label className="label">{t('cert_form.expiration', 'Vencimiento (Opcional)')}</label>
            <input type="date" className="input-field" value={formData.expirationDate || ''} onChange={(e) => updateField('expirationDate', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.notes_hours', 'Notas Adicionales / Horas Totales')}</label>
          <textarea className="input-field" rows={3} placeholder="Ej. Cumplidas 45 horas de instrucción práctica..." value={formData.notes || ''} onChange={(e) => updateField('notes', e.target.value)} />
        </div>

        <div className="form-group file-upload-area" style={{
          border: '2px dashed #334155',
          padding: '32px 24px',
          textAlign: 'center',
          borderRadius: '12px',
          cursor: 'pointer',
          background: 'rgba(30, 41, 59, 0.5)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }} onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#3b82f6';
          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
        }} onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#334155';
          e.currentTarget.style.background = 'rgba(30, 41, 59, 0.5)';
        }}>
          <i className="fas fa-cloud-upload-alt" style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '12px', display: 'block' }}></i>
          <p style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '500', marginBottom: '4px' }}>{t('cert_form.attach_pdf', 'Adjuntar PDF o Escaneo')}</p>
          <p style={{ fontSize: '12px', color: '#64748b' }}>Logbook/Formulario</p>
        </div>

        <button type="submit" disabled={isLoading} className="btn-submit">
          <i className="fas fa-file-signature"></i>
          {isLoading ? t('cert_form.processing', 'Procesando...') : t('cert_form.sign_issue_maint', 'Firmar y Emitir Certificado de Mantenimiento')}
        </button>
        <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px', textAlign: 'center' }}>
          <i className="fas fa-lock"></i> {t('cert_form.digital_signature_note', 'Al emitir, se generará una firma digital única imposible de falsificar.')}
        </p>
      </>) : (<>
        <div className="grid-2">
          <div className="form-group">
            <label className="label">
              {t('cert_form.aircraft_model', 'Modelo de Aeronave')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input type="text" className={clsx('input-field', errors.aircraftModel && 'error')} placeholder="Ej. Cessna 172, Piper PA-28" value={formData.aircraftModel || ''} onChange={(e) => updateField('aircraftModel', e.target.value)} />
            {errors.aircraftModel && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.aircraftModel}</p>)}
          </div>

          <div className="form-group">
            <label className="label">
              {t('cert_form.registration', 'Matrícula')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input type="text" className={clsx('input-field', errors.registration && 'error')} placeholder="Ej. LV-GHT, N123AB" value={formData.registration || ''} onChange={(e) => updateField('registration', e.target.value)} />
            {errors.registration && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.registration}</p>)}
          </div>
        </div>

        <div className="form-group">
          <label className="label">
            {t('cert_form.maintenance_type', 'Tipo de Mantenimiento')} <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select className={clsx('select-field', errors.maintenanceType && 'error')} value={formData.maintenanceType || ''} onChange={(e) => updateField('maintenanceType', e.target.value)}>
            {MAINTENANCE_TYPES.map((type) => (<option key={type.value} value={type.value}>
              {type.label}
            </option>))}
          </select>
          {errors.maintenanceType && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.maintenanceType}</p>)}
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="label">{t('cert_form.issue_date', 'Fecha de Emisión')} <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="date" className={clsx('input-field', errors.issueDate && 'error')} value={formData.issueDate || ''} onChange={(e) => updateField('issueDate', e.target.value)} />
            {errors.issueDate && (<p style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px' }}>{errors.issueDate}</p>)}
          </div>

          <div className="form-group">
            <label className="label">{t('cert_form.expiration', 'Vencimiento (Opcional)')}</label>
            <input type="date" className="input-field" value={formData.expirationDate || ''} onChange={(e) => updateField('expirationDate', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="label">{t('cert_form.flight_hours', 'Horas de Vuelo Totales')}</label>
            <input type="number" className="input-field" placeholder="Ej. 1250.5" value={formData.flightHours || ''} onChange={(e) => updateField('flightHours', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="label">{t('cert_form.work_order', 'Número de Orden de Trabajo')}</label>
            <input type="text" className="input-field" placeholder="Ej. WO-2024-001" value={formData.workOrderNumber || ''} onChange={(e) => updateField('workOrderNumber', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="label">{t('cert_form.last_inspection', 'Fecha Última Inspección')}</label>
            <input type="date" className="input-field" value={formData.lastInspectionDate || ''} onChange={(e) => updateField('lastInspectionDate', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="label">{t('cert_form.next_inspection', 'Próxima Inspección')}</label>
            <input type="date" className="input-field" value={formData.nextInspectionDate || ''} onChange={(e) => updateField('nextInspectionDate', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.responsible_tech', 'Técnico Responsable')}</label>
          <input type="text" className="input-field" placeholder="Ej. Ing. Carlos Rodríguez" value={formData.technicianName || ''} onChange={(e) => updateField('technicianName', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.findings', 'Hallazgos / Defectos Encontrados')}</label>
          <textarea className="input-field" rows={3} placeholder="Describa los hallazgos encontrados durante la inspección..." value={formData.findings || ''} onChange={(e) => updateField('findings', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.corrective_actions', 'Acciones Correctivas Realizadas')}</label>
          <textarea className="input-field" rows={3} placeholder="Describa las acciones correctivas realizadas..." value={formData.correctiveActions || ''} onChange={(e) => updateField('correctiveActions', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.parts_replaced', 'Partes Reemplazadas')}</label>
          <textarea className="input-field" rows={2} placeholder="Liste las partes reemplazadas (número de parte, descripción)..." value={formData.partsReplaced || ''} onChange={(e) => updateField('partsReplaced', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="label">{t('cert_form.additional_notes', 'Notas Adicionales')}</label>
          <textarea className="input-field" rows={3} placeholder="Observaciones adicionales, recomendaciones, etc." value={formData.notes || ''} onChange={(e) => updateField('notes', e.target.value)} />
        </div>

        <div className="form-group" style={{ border: '2px dashed #334155', padding: '20px', textAlign: 'center', borderRadius: '6px', cursor: 'pointer' }}>
          <i className="fas fa-cloud-upload-alt" style={{ fontSize: '24px', color: '#94a3b8', marginBottom: '10px' }}></i>
          <p style={{ fontSize: '13px', color: '#94a3b8' }}>{t('cert_form.attach_maintenance', 'Adjuntar PDF o Escaneo (Formulario de Mantenimiento, Logbook, etc.)')}</p>
        </div>

        <button type="submit" disabled={isLoading} className="btn-submit">
          <i className="fas fa-file-signature"></i>
          {isLoading ? t('cert_form.processing', 'Procesando...') : t('cert_form.sign_issue_maint', 'Firmar y Emitir Certificado de Mantenimiento')}
        </button>
        <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px', textAlign: 'center' }}>
          <i className="fas fa-lock"></i> {t('cert_form.digital_signature_note', 'Al emitir, se generará una firma digital única imposible de falsificar.')}
        </p>
      </>)}
    </form>
  </div>);
};
